<script setup>
/**
 * The whole task on one page: every step, and where each one leads. THE SPEC, §4.
 *
 * READ-ONLY, AND THAT IS THE DESIGN RATHER THAN A STAGE OF IT. The step cards are where a
 * task is written; this is where an owner checks the SHAPE of what she wrote — that the
 * booking really does come back to the day question when a day is full, that the branch she
 * added last week is still attached to something. "A draggable canvas" is listed under Out
 * of scope in the spec, so there is no dragging, no zoom and no pan chrome here: the map
 * scrolls like a wide table and a box is a link to its card.
 *
 * WHY INLINE SVG AND NOT A DIAGRAM LIBRARY. `helpers/task-map-layout.js` carries the full
 * reasoning; the short version is that a dependency would mean editing `package.json` (an
 * upstream file, so a merge conflict on every Chatwoot bump) and adopting a second palette
 * that `waslo-lint.mjs` cannot see into. Drawn here, every colour is an `n-*` class checked
 * by the same lint as the rest of the section, in both themes, with nothing to synchronise.
 * `TrendChart.vue` made the same trade for the same reasons.
 *
 * EVERYTHING GEOMETRIC IS IN THE HELPER. This file positions nothing and computes no
 * coordinate: it translates tokens into words, maps a tone onto two classes, and renders.
 * That split is what lets the awkward half — cycles, unreachable steps, a link to a step
 * that was deleted — be tested as a pure function instead of through a mount.
 *
 * DIRECTION IS PINNED LTR, like the activity chart and for the same reason: the map is a
 * diagram whose arrows mean "next", and mirroring it on an Arabic dashboard would turn the
 * first step into the last one. The WORDS are still translated, and a step's own name is
 * whatever the owner typed, in whichever script she typed it.
 */
import { computed } from 'vue';

import CardLayout from 'dashboard/components-next/CardLayout.vue';

import { useWasloI18n } from '../i18n.js';
import { toIntlLocale } from '../helpers/intl.js';
import { taskMapLayout, TEXT_Y } from '../helpers/task-map-layout.js';

const props = defineProps({
  /**
   * The stored step graph, exactly as the API returned it — `{ firstStepId, steps }` — or
   * `null` for a task that is still a plain list of written steps.
   */
  definition: { type: Object, default: null },
});

const emit = defineEmits(['select']);

const { t, locale } = useWasloI18n();

const lang = computed(() =>
  String(locale.value).startsWith('ar') ? 'ar' : 'en'
);

const map = computed(() =>
  taskMapLayout(props.definition, { lang: lang.value })
);

/** Arabic-Indic digits on an Arabic dashboard, like every other count in this section. */
const count = computed(() => {
  const format = new Intl.NumberFormat(toIntlLocale(locale.value));
  return value => format.format(value);
});

/**
 * The box's two colours per tone. Never `n-brand` — see `TrendChart.vue`: that token is a
 * fixed Chatwoot-blue hex that the branding overlay recolours by enumerating exact utility
 * selectors, and `fill-n-brand` is not one of them, so it would render blue in an emerald
 * product in both themes. `n-blue-*` is genuinely variable-driven and remapped.
 */
const TONE = {
  plain: { box: 'fill-n-solid-2 stroke-n-slate-6', dash: false },
  first: { box: 'fill-n-blue-3 stroke-n-blue-9', dash: false },
  orphan: { box: 'fill-n-amber-3 stroke-n-amber-8', dash: false },
  // Not a second amber fill: "nothing follows" is a milder thing than "this can never
  // run", and two identical boxes for two different problems is how an owner fixes the
  // wrong one. Same border colour, drawn open.
  deadEnd: { box: 'fill-n-solid-2 stroke-n-amber-8', dash: true },
  broken: { box: 'fill-n-ruby-3 stroke-n-ruby-9', dash: false },
};

const BADGE = {
  orphan: { key: 'WASLO.TASKS.MAP.BADGE.ORPHAN', class: 'fill-n-amber-11' },
  deadEnd: { key: 'WASLO.TASKS.MAP.BADGE.DEAD_END', class: 'fill-n-amber-11' },
  broken: { key: 'WASLO.TASKS.MAP.BADGE.BROKEN', class: 'fill-n-ruby-11' },
};

/**
 * What the step does, in the owner's words. `unknown` is a step type this build has never
 * heard of — a task saved by a newer Waslo — and it says so rather than pretending the step
 * is not there.
 */
const TYPE_KEY = {
  ask: 'WASLO.TASKS.MAP.TYPE.ASK',
  choose: 'WASLO.TASKS.MAP.TYPE.CHOOSE',
  choose_live: 'WASLO.TASKS.MAP.TYPE.CHOOSE_LIVE',
  call: 'WASLO.TASKS.MAP.TYPE.CALL',
  tell: 'WASLO.TASKS.MAP.TYPE.TELL',
  handoff: 'WASLO.TASKS.MAP.TYPE.HANDOFF',
  end: 'WASLO.TASKS.MAP.TYPE.END',
  unknown: 'WASLO.TASKS.MAP.TYPE.UNKNOWN',
};

/** The branch words. `then` is deliberately absent — see `linkLabel`. */
const KIND_KEY = {
  worked: 'WASLO.TASKS.MAP.LINK.WORKED',
  failed: 'WASLO.TASKS.MAP.LINK.FAILED',
  none: 'WASLO.TASKS.MAP.LINK.NONE',
};

const typeLabel = step => t(TYPE_KEY[step.type] ?? TYPE_KEY.unknown);

const badgeOf = step => BADGE[step.tone] ?? null;

/**
 * What is written along an arrow.
 *
 * An unconditional next step gets NO label. "then" on fourteen of the sixteen arrows is not
 * information, it is the same word fourteen times over a diagram whose arrows already point
 * downwards; the word exists in the vocabulary for the step cards, where a branch really
 * does need naming in prose. Here only the arrows that are conditional say anything.
 */
const linkLabel = link => {
  const parts = link.parts
    .map(part => {
      if (part.kind === 'option') return part.label;
      return KIND_KEY[part.kind] ? t(KIND_KEY[part.kind]) : '';
    })
    .filter(Boolean);
  if (link.extra > 0) {
    parts.push(
      t('WASLO.TASKS.MAP.LINK.MORE', { count: count.value(link.extra) })
    );
  }
  return parts.join(t('WASLO.COMMON.LIST_SEPARATOR'));
};

/** The hover title on a box: its full name, then whatever is wrong with it. */
const boxTitle = step => {
  const lines = [step.name, typeLabel(step)];
  if (step.isFirst) lines.push(t('WASLO.TASKS.MAP.BADGE.FIRST'));
  const badge = badgeOf(step);
  if (badge) lines.push(t(badge.key));
  return lines.join(t('WASLO.COMMON.LIST_SEPARATOR'));
};

/**
 * Only the marks that are actually on this map. A permanent four-row key under a task with
 * nothing wrong with it teaches an owner to expect problems that are not there.
 */
const legend = computed(() => {
  const rows = [];
  if (map.value.hasFirstStep) {
    rows.push({
      key: 'first',
      label: t('WASLO.TASKS.MAP.BADGE.FIRST'),
      swatch: 'bg-n-blue-9',
    });
  }
  const tones = new Set(map.value.flagged.map(step => step.tone));
  if (tones.has('orphan')) {
    rows.push({
      key: 'orphan',
      label: t('WASLO.TASKS.MAP.BADGE.ORPHAN'),
      swatch: 'bg-n-amber-8',
    });
  }
  if (tones.has('deadEnd')) {
    rows.push({
      key: 'deadEnd',
      label: t('WASLO.TASKS.MAP.BADGE.DEAD_END'),
      swatch: 'bg-n-amber-8',
    });
  }
  if (tones.has('broken')) {
    rows.push({
      key: 'broken',
      label: t('WASLO.TASKS.MAP.BADGE.BROKEN'),
      swatch: 'bg-n-ruby-9',
    });
  }
  return rows;
});

const select = stepId => emit('select', stepId);
</script>

<template>
  <div class="flex flex-col gap-4" data-waslo-task-map>
    <p class="mb-0 text-sm text-n-slate-11">
      {{ t('WASLO.TASKS.MAP.DESCRIPTION') }}
    </p>

    <!-- Nothing to draw: a task that is still a written list of steps, or a graph with no
         steps in it yet. Both are ordinary states, so neither is an error. -->
    <CardLayout v-if="map.isEmpty">
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('WASLO.TASKS.MAP.EMPTY') }}
      </p>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('WASLO.TASKS.MAP.EMPTY_BODY') }}
      </p>
    </CardLayout>

    <template v-else>
      <!-- The one thing a map cannot draw around: with no starting step the engine has
           nowhere to begin, so every step below is floating and saying so once is kinder
           than colouring all sixteen of them amber. -->
      <p v-if="!map.hasFirstStep" class="mb-0 text-sm text-n-amber-11">
        {{ t('WASLO.TASKS.MAP.NO_FIRST') }}
      </p>

      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('WASLO.TASKS.MAP.COUNT', { count: count(map.stepCount) }) }}
      </p>

      <!--
        Below `md` the map is not shown at all, and this is not a shrug: sixteen boxes
        squeezed into 360 points is a picture OF a graph rather than a readable one, and
        scaling the type down with it takes the step names below legibility. The step cards
        are the narrow-screen answer and they are one tab away.
      -->
      <p class="mb-0 text-sm text-n-slate-11 md:hidden">
        {{ t('WASLO.TASKS.MAP.NARROW') }}
      </p>

      <!--
        Natural size inside a horizontal scroller rather than `w-full h-auto`: a wide graph
        squeezed to the column width is unreadable, and one unit of the layout is one CSS
        pixel precisely so the type inside it lands where the rest of the page's type does.
        `dir="ltr"` on the scroller as well as the drawing, so the scroll starts at the
        first step on an Arabic dashboard instead of at the far end of the graph.
      -->
      <div class="hidden overflow-x-auto md:block" dir="ltr">
        <svg
          :width="map.width"
          :height="map.height"
          :viewBox="`0 0 ${map.width} ${map.height}`"
          direction="ltr"
          role="group"
          :aria-label="
            t('WASLO.TASKS.MAP.ARIA', { count: count(map.stepCount) })
          "
        >
          <!-- Lines first, so a box always sits on top of the arrows that reach it. -->
          <g v-for="link in map.links" :key="link.key">
            <path
              :d="link.path"
              class="stroke-n-slate-7"
              fill="none"
              stroke-width="1.5"
              :stroke-dasharray="link.isBack ? '4 3' : undefined"
            />
            <polygon :points="link.arrow" class="fill-n-slate-8" />
            <text
              v-if="linkLabel(link)"
              :x="link.labelX"
              :y="link.labelY"
              :text-anchor="link.labelAnchor"
              class="text-[10px] fill-n-slate-10"
            >
              {{ linkLabel(link) }}
            </text>
          </g>

          <g
            v-for="step in map.steps"
            :key="step.id"
            role="button"
            tabindex="0"
            class="cursor-pointer"
            :aria-label="t('WASLO.TASKS.MAP.OPEN', { name: step.name })"
            :data-waslo-map-step="step.id"
            @click="select(step.id)"
            @keydown.enter.prevent="select(step.id)"
            @keydown.space.prevent="select(step.id)"
          >
            <title>{{ boxTitle(step) }}</title>
            <!-- Outside the box, above it: the first step is the one thing on this map an
                 owner looks for before anything else. -->
            <text
              v-if="step.isFirst"
              :x="step.x"
              :y="step.y - 8"
              class="text-[10px] font-medium fill-n-blue-11"
            >
              {{ t('WASLO.TASKS.MAP.BADGE.FIRST') }}
            </text>
            <rect
              :x="step.x"
              :y="step.y"
              :width="step.w"
              :height="step.h"
              rx="8"
              stroke-width="1.5"
              :stroke-dasharray="TONE[step.tone].dash ? '5 3' : undefined"
              :class="TONE[step.tone].box"
            />
            <text
              :x="step.x + 12"
              :y="step.y + TEXT_Y.type"
              class="text-[10px] fill-n-slate-10"
            >
              {{ typeLabel(step) }}
            </text>
            <text
              v-for="(line, index) in step.nameLines"
              :key="index"
              :x="step.x + 12"
              :y="step.y + TEXT_Y.name[index]"
              class="text-[12px] font-medium fill-n-slate-12"
            >
              {{ line }}
            </text>
            <text
              v-if="badgeOf(step)"
              :x="step.x + 12"
              :y="step.y + TEXT_Y.badge"
              class="text-[10px]"
              :class="badgeOf(step).class"
            >
              {{ t(badgeOf(step).key) }}
            </text>
          </g>
        </svg>
      </div>

      <!-- The key, HTML rather than SVG: it is prose with swatches and it has to wrap. -->
      <div
        v-if="legend.length"
        class="flex flex-wrap items-center gap-x-5 gap-y-2"
      >
        <span
          v-for="row in legend"
          :key="row.key"
          class="inline-flex items-center gap-2 text-sm text-n-slate-11"
        >
          <span
            class="rounded-sm size-2.5"
            :class="row.swatch"
            aria-hidden="true"
          />
          {{ row.label }}
        </span>
      </div>

      <!--
        The same problems again, as a list. The colours say WHERE each one is; this says
        THAT there is one, which is the half a diagram cannot do — nobody scans sixteen
        boxes for a different border. It is also the only route to them on a narrow screen,
        where the drawing is not rendered at all.
      -->
      <CardLayout v-if="map.flagged.length">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('WASLO.TASKS.MAP.CHECK') }}
        </p>
        <ul class="flex flex-col gap-2 mb-0 list-none">
          <li v-for="step in map.flagged" :key="step.id">
            <button
              type="button"
              class="flex flex-wrap items-baseline gap-x-2 text-sm text-start"
              :data-waslo-map-flagged="step.id"
              @click="select(step.id)"
            >
              <span class="font-medium text-n-slate-12" dir="auto">
                {{ step.name }}
              </span>
              <span
                :class="
                  step.tone === 'broken' ? 'text-n-ruby-11' : 'text-n-amber-11'
                "
              >
                {{ t(badgeOf(step).key) }}
              </span>
            </button>
          </li>
        </ul>
      </CardLayout>
    </template>
  </div>
</template>
